import { supabase } from "./client";

const MZ_RECORD_USAGE_FUNCTION_NAME = "mz-record-usage";

const toSafeText = (value, maxLength = 240) =>
  String(value || "")
    .trim()
    .slice(0, maxLength);

const toWholeUnits = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? Math.max(0, Math.round(parsed)) : 0;
};

const normalizeFunctionError = (error, data, fallbackMessage) => {
  const transportMessage = toSafeText(error?.message, 240);
  const backendMessage = toSafeText(
    data?.error || data?.message || data?.details || data?.hint,
    240
  );

  if (backendMessage && transportMessage.toLowerCase().includes("non-2xx")) {
    return backendMessage;
  }

  return backendMessage || transportMessage || fallbackMessage;
};

const extractFunctionErrorPayload = async (error, data) => {
  if (data && typeof data === "object") {
    return data;
  }

  const response = error?.context;

  if (!response) {
    return null;
  }

  const readableResponse =
    typeof response.clone === "function" ? response.clone() : response;

  try {
    const contentType = toSafeText(
      readableResponse.headers?.get?.("content-type"),
      120
    ).toLowerCase();

    if (contentType.includes("application/json")) {
      return await readableResponse.json();
    }

    const rawText = toSafeText(await readableResponse.text(), 500);

    if (!rawText) {
      return null;
    }

    try {
      return JSON.parse(rawText);
    } catch {
      return { error: rawText };
    }
  } catch {
    return null;
  }
};

export const normalizeMzRuntimeUsageResult = (value = {}) => ({
  ok: value?.ok !== false,
  allowed: value?.allowed !== false,
  blocked: value?.blocked === true || value?.allowed === false,
  applied: value?.applied === true,
  alreadyApplied: value?.alreadyApplied === true,
  skipped: value?.skipped === true,
  reason: toSafeText(value?.reason || "", 120),
  eventKey: toSafeText(value?.eventKey || "", 80),
  eventLabel: toSafeText(value?.eventLabel || "", 160),
  usageEventId: toSafeText(value?.usageEventId || "", 120),
  unitsConsumed: toWholeUnits(value?.unitsConsumed),
  unitsRequired: toWholeUnits(value?.unitsRequired ?? value?.requiredUnits),
  balanceBeforeUnits: Number.isFinite(Number(value?.balanceBeforeUnits))
    ? Math.round(Number(value.balanceBeforeUnits))
    : null,
  balanceAfterUnits: Number.isFinite(Number(value?.balanceAfterUnits))
    ? Math.round(Number(value.balanceAfterUnits))
    : null,
});

export const recordMzRuntimeUsage = async ({
  branchId,
  eventKey,
  orderId,
  paymentOperationId,
  paymentIntentId = "",
  sourceChannel = "unknown",
  sourceMode = "unknown",
  metadata = {},
} = {}) => {
  if (!supabase) {
    throw new Error(
      "Supabase no esta configurado. No podemos registrar el consumo MZ."
    );
  }

  const normalizedBranchId = toSafeText(branchId, 120);
  const normalizedEventKey = toSafeText(eventKey, 120);
  const normalizedOrderId = toSafeText(orderId, 160);
  const normalizedPaymentOperationId = toSafeText(paymentOperationId, 160);

  if (!normalizedBranchId || !normalizedEventKey) {
    throw new Error("Faltan datos basicos para registrar el consumo MZ.");
  }

  const body = {
    branchId: normalizedBranchId,
    eventKey: normalizedEventKey,
    orderId: normalizedOrderId,
    paymentOperationId: normalizedPaymentOperationId,
    paymentIntentId: toSafeText(paymentIntentId, 120),
    sourceChannel: toSafeText(sourceChannel, 80) || "unknown",
    sourceMode: toSafeText(sourceMode, 80) || "unknown",
    metadata:
      metadata && typeof metadata === "object" && !Array.isArray(metadata)
        ? metadata
        : {},
  };

  const { data, error } = await supabase.functions.invoke(
    MZ_RECORD_USAGE_FUNCTION_NAME,
    { body }
  );

  if (error) {
    const errorPayload = await extractFunctionErrorPayload(error, data);

    throw new Error(
      normalizeFunctionError(
        error,
        errorPayload,
        "No pudimos registrar el consumo MZ."
      )
    );
  }

  return normalizeMzRuntimeUsageResult(data);
};

export const checkMzRuntimeUsageAvailability = async ({
  branchId,
  eventKey,
  paymentIntentId = "",
  sourceChannel = "unknown",
  sourceMode = "unknown",
  metadata = {},
} = {}) => {
  if (!supabase) {
    throw new Error(
      "Supabase no esta configurado. No podemos validar el saldo MZ."
    );
  }

  const normalizedBranchId = toSafeText(branchId, 120);
  const normalizedEventKey = toSafeText(eventKey, 120);

  if (!normalizedBranchId || !normalizedEventKey) {
    throw new Error("Faltan datos basicos para validar el saldo MZ.");
  }

  const body = {
    checkOnly: true,
    branchId: normalizedBranchId,
    eventKey: normalizedEventKey,
    paymentIntentId: toSafeText(paymentIntentId, 120),
    sourceChannel: toSafeText(sourceChannel, 80) || "unknown",
    sourceMode: toSafeText(sourceMode, 80) || "unknown",
    metadata:
      metadata && typeof metadata === "object" && !Array.isArray(metadata)
        ? metadata
        : {},
  };

  const { data, error } = await supabase.functions.invoke(
    MZ_RECORD_USAGE_FUNCTION_NAME,
    { body }
  );

  if (error) {
    const errorPayload = await extractFunctionErrorPayload(error, data);

    throw new Error(
      normalizeFunctionError(
        error,
        errorPayload,
        "No pudimos validar el saldo MZ."
      )
    );
  }

  return normalizeMzRuntimeUsageResult(data);
};
